import { Button, Flex, RadioCard, SimpleGrid } from '@chakra-ui/react';
import { FeatureFlag } from '@fluenceur/types';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInvitationActions, useInvitationStoreSelector } from '@/contexts/invitation-context';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useUgcAvailability } from '@/hooks/use-ugc-availability';
import { isIdentityLocked } from '@/components/plan-locks/creator-locks';
import { formatCents } from '@/libs/utils/format-money';
import { recipientNameFor } from './collab/collab-wizard-helpers';
import { useCollabRecipients } from './collab/use-collab-recipients';
import { useRecipientUgcOffer } from './collab/use-recipient-ugc-offer';
import CollabTypeCard from './collab-type-card';
import { COLLAB_TYPE_OPTIONS, type CollabType } from './collab-types';

interface TypeSelectionViewProps {
  onClose: () => void;
}

/**
 * First step of the "propose a collaboration" flow: pick a collaboration type.
 * Nothing is pre-selected (per the validated design), except UGC for a creator
 * whose UGC badge is active, when the brand is offered UGC (the offer the
 * contact sheet passed, else the ugc-terms answer for the recipients): Continue stays
 * disabled until a choice is made. Affiliation hands off to the existing
 * program flow, Sponsored post and UGC enter the collab wizard's brief step.
 * Its actions live in the body (no shell footer slot).
 */
const TypeSelectionView = ({ onClose }: TypeSelectionViewProps) => {
  const { t, i18n } = useTranslation();
  const collabType = useInvitationStoreSelector((state) => state.collabType);
  const creator = useInvitationStoreSelector((state) => state.creator);
  const recipientEmail = useInvitationStoreSelector((state) => state.recipientEmail);
  const target = useInvitationStoreSelector((state) => state.target);
  const ugcOffer = useInvitationStoreSelector((state) => state.collabRecipientUgcOffer);
  const { setCollabType, setCurrentView } = useInvitationActions();
  const [picked, setPicked] = useState<CollabType | null>(collabType ?? null);
  const collabsEnabled = useFeatureFlag(FeatureFlag.COLLABS);
  const ugcAvailable = useUgcAvailability();
  const recipientUgc = useRecipientUgcOffer(ugcAvailable && !ugcOffer);
  const recipientOffersUgc = recipientUgc.offersUgc;
  const recipients = useCollabRecipients();
  // Her starting price per video, a reference beside the UGC card when one
  // creator is proposed; never a rule, the terms step compares it again.
  const startingOffer = ugcOffer ?? recipientUgc.monoOffer;
  const ugcStartingPrice =
    startingOffer && recipients.length === 1
      ? t('invitations.modal.collab.wizard.terms.ugc.startingPrice', {
          name: recipientNameFor(recipients[0], t),
          price: formatCents(
            startingOffer.startingPriceCents,
            startingOffer.currency,
            i18n.language
          ),
        })
      : undefined;
  // Derived rather than seeded, so a badge read before the flags (or the
  // ugc-terms answer) arrive still preselects UGC once the brand is known to
  // be offered it.
  const selected: CollabType | null =
    picked ?? ((ugcOffer || recipientOffersUgc) && ugcAvailable ? 'ugc' : null);

  // Affiliation mints a program invitation, which the backend can only address
  // through an email or a social profile. An identity-only contact (no creator,
  // no email, no seeded target — including a failed creator fetch) has neither:
  // the card is disabled with the requirement as its subline. The sponsored
  // wizard delivers by personal link, so it stays open.
  const canAffiliate = !!(creator || recipientEmail || target);
  // A prospect the brand's plan masks cannot be invited to a programme (the API
  // refuses it); proposing a collaboration stays open.
  const affiliationLocked = isIdentityLocked(creator);

  // Sponsored posts ride the COLLABS flag — without it the kind shows as a
  // disabled "soon" card so the brand can't enter a wizard the backend rejects.
  // UGC is hidden, not shown as coming, from a brand that is not offered it:
  // without its Shopify store it never will be until it connects one.
  const options = useMemo(
    () =>
      COLLAB_TYPE_OPTIONS.filter((option) => option.type !== 'ugc' || ugcAvailable).map(
        (option) => {
          if (option.type === 'sponsored' && !collabsEnabled) {
            return { ...option, enabled: false, badge: 'soon' as const };
          }
          if (option.type === 'affiliation' && affiliationLocked) {
            return { ...option, enabled: false, descriptionKey: 'planLocks.tooltip' };
          }
          if (option.type === 'affiliation' && !canAffiliate) {
            return {
              ...option,
              enabled: false,
              descriptionKey: 'invitations.modal.collab.types.affiliation.requiresContact',
            };
          }
          return option;
        }
      ),
    [collabsEnabled, ugcAvailable, canAffiliate, affiliationLocked]
  );

  const handleContinue = () => {
    if (!selected) return;
    // Guard the seeded-selection edge: a disabled kind can't be reached by click,
    // but a pre-seeded `collabType` could land here with its card now disabled.
    if (!options.find((option) => option.type === selected)?.enabled) return;
    setCollabType(selected);
    setCurrentView(
      selected === 'sponsored' || selected === 'ugc' ? 'collabBrief' : 'programSelection'
    );
  };

  return (
    <Flex direction="column" gap="4">
      <RadioCard.Root
        value={selected ?? ''}
        onValueChange={(details) => {
          if (details.value) setPicked(details.value as CollabType);
        }}
        colorPalette="primary"
      >
        <SimpleGrid columns={2} gap="3">
          {options.map((option) => (
            <CollabTypeCard
              key={option.type}
              option={option}
              note={option.type === 'ugc' ? ugcStartingPrice : undefined}
            />
          ))}
        </SimpleGrid>
      </RadioCard.Root>

      <Flex justify="flex-end" gap="2" pt="1">
        <Button variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button colorPalette="primary" onClick={handleContinue} disabled={!selected}>
          {t('invitations.modal.collab.continue')}
        </Button>
      </Flex>
    </Flex>
  );
};

export default TypeSelectionView;
